// ============================================================================
// OddsButton — sportsbook pill
// ============================================================================
// One selection. Two layouts, and the HOST picks (Atlas Universe recipe):
//
//   stacked (default) — price above the label. Every 2-3-across market.
//   row               — label left, price right, one line. A full-width
//                       outcome inside a market list, so stacked outcomes
//                       share one decimal axis.
//
// Source order is label-then-price in both, so the accessibility tree reads
// "Arsenal 1.85"; `stacked` reverses it visually in CSS.
//
// Click handler is wired by the page-level betslip integration; here it's
// purely visual.
// ============================================================================

import type { ReactElement } from 'react';
import styles from './OddsButton.module.css';

export interface OddsButtonProps {
  /** Selection label — '1' / 'X' / '2' / team name / 'Over 2.5' */
  label: string;
  /** Decimal odds, e.g. 2.48. null/undefined = suspended. */
  odds: number | null | undefined;
  /** Width behaviour. 'fill' grows in flex containers; 'auto' hugs content. */
  width?: 'fill' | 'auto';
  /**
   * 'stacked' (default) puts the price over the label; 'row' puts the label
   * left and the price right. Presentation only — the DOM order and the
   * accessible name are identical.
   */
  layout?: 'stacked' | 'row';
  /** Suspended renders greyed out with strike-through odds. */
  suspended?: boolean;
  /** True when this outcome is the currently selected one in its market. */
  selected?: boolean;
  /** Click handler — typically wired to a betslip add. */
  onClick?: () => void;
  /** Optional aria override; defaults to '{label} {odds}'. */
  ariaLabel?: string;
}

// LOCKED HOUSE RULE (2026-06-11): decimal odds PRICES always render with
// "." on every market and locale — European odds notation. Market LINES
// (e.g. "Över 2,5") and statistics keep following locale rules; that
// formatting lives with the callers. This component therefore takes no
// separator: a price is a price.
function formatOdds(odds: number | null | undefined): string {
  if (odds == null) return '—';
  return odds.toFixed(2);
}

export function OddsButton({
  label,
  odds,
  width = 'fill',
  layout = 'stacked',
  suspended,
  selected,
  onClick,
  ariaLabel,
}: OddsButtonProps): ReactElement {
  const isSuspended = suspended || odds == null;
  const widthClass = width === 'fill' ? styles.fill : styles.auto;
  const layoutClass = layout === 'row' ? styles.row : styles.stacked;
  const stateClass = isSuspended
    ? styles.suspended
    : selected
      ? styles.selected
      : '';

  const oddsLabel = formatOdds(odds);

  return (
    <button
      type="button"
      className={`${styles.btn} ${layoutClass} ${widthClass} ${stateClass}`}
      onClick={isSuspended ? undefined : onClick}
      disabled={isSuspended}
      aria-pressed={selected ? true : undefined}
      aria-label={ariaLabel ?? `${label} ${oddsLabel}`}
    >
      <span className={styles.label}>{label}</span>
      <span className={styles.odds}>{oddsLabel}</span>
    </button>
  );
}

export default OddsButton;
